// Admin-side notification primitives: sound library + favicon flash + browser popup + prefs.
// Extended vs the reference: 4 sound styles, per-event routing matrix, hourly rate limit, weekday-aware quiet hours.

let audioCtx = null;
function ctx() {
  if (!audioCtx) {
    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) return null;
    audioCtx = new Ctor();
  }
  if (audioCtx.state === "suspended") audioCtx.resume();
  return audioCtx;
}

const SOUND_LIBRARY = {
  chime: [{ f: 880, t: 0.0, d: 0.18 }, { f: 1318.5, t: 0.18, d: 0.22 }], // A5 → E6
  ping:  [{ f: 1200, t: 0.0, d: 0.08 }],
  bell:  [{ f: 1046.5, t: 0.0, d: 0.12 }, { f: 783.99, t: 0.12, d: 0.18 }, { f: 1046.5, t: 0.30, d: 0.24 }],
  ding:  [{ f: 660, t: 0.0, d: 0.10 }, { f: 990, t: 0.10, d: 0.30 }],
};

export const SOUND_STYLES = Object.keys(SOUND_LIBRARY);

export function playSound(style = "chime", volume = 0.18) {
  try {
    const ac = ctx();
    if (!ac) return;
    const now = ac.currentTime;
    const notes = SOUND_LIBRARY[style] || SOUND_LIBRARY.chime;
    notes.forEach(({ f, t, d }) => {
      const osc = ac.createOscillator();
      const g = ac.createGain();
      osc.type = "sine";
      osc.frequency.value = f;
      g.gain.setValueAtTime(0.0001, now + t);
      g.gain.exponentialRampToValueAtTime(volume, now + t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, now + t + d);
      osc.connect(g).connect(ac.destination);
      osc.start(now + t);
      osc.stop(now + t + d);
    });
  } catch { /* ignore */ }
}

let faviconTimer = null;
let originalFavicon = null;
export function flashFavicon(color = "#E11D48") {
  try {
    const link = document.querySelector("link[rel='icon']") || document.querySelector("link[rel='shortcut icon']");
    if (!link) return;
    if (!originalFavicon) originalFavicon = link.href;
    const c = document.createElement("canvas");
    c.width = 32; c.height = 32;
    const g = c.getContext("2d");
    g.fillStyle = color;
    g.beginPath(); g.arc(16, 16, 14, 0, Math.PI * 2); g.fill();
    g.fillStyle = "#fff";
    g.font = "bold 20px sans-serif";
    g.textAlign = "center";
    g.textBaseline = "middle";
    g.fillText("!", 16, 17);
    link.href = c.toDataURL("image/png");
    if (faviconTimer) clearTimeout(faviconTimer);
    faviconTimer = setTimeout(() => { if (originalFavicon) link.href = originalFavicon; faviconTimer = null; }, 10000);
  } catch { /* */ }
}

export function browserNotify(title, body, onClick) {
  try {
    if (!("Notification" in window)) return;
    if (Notification.permission !== "granted") return;
    if (document.visibilityState === "visible") return;
    const n = new Notification(title, { body, tag: "nexon-admin" });
    if (onClick) n.onclick = onClick;
  } catch { /* */ }
}

export async function requestDesktopPermission() {
  if (!("Notification" in window)) return "unsupported";
  if (Notification.permission === "granted") return "granted";
  const r = await Notification.requestPermission();
  return r;
}

const PREFS_KEY = "nexon_admin_notif_prefs_v2";
const LOG_KEY = "nexon_admin_notif_log";

export const EVENTS = [
  { key: "new_chat", label: "New live chat", desc: "Any new incoming customer chat" },
  { key: "manager_requested", label: "Manager requested", desc: "Customer asked for a human / Miles handed off" },
  { key: "new_submission", label: "New submission", desc: "A gift-card submission was created" },
  { key: "fraud_flag", label: "Fraud flag", desc: "Auto-detected suspicious activity" },
  { key: "new_email", label: "Support email", desc: "New RED-flagged email needs review" },
  { key: "payout_complete", label: "Payout complete", desc: "Customer confirmed a payout received" },
];

export const CHANNELS = [
  { key: "sound", label: "Sound" },
  { key: "desktop", label: "Desktop popup" },
  { key: "favicon", label: "Favicon flash" },
  { key: "push", label: "Phone push" },
  { key: "webhook", label: "Slack/Discord" },
];

const defaults = {
  enabled: true,
  sound_style: "chime",
  volume: 0.5,
  quiet_start: "",
  quiet_end: "",
  quiet_days: [], // 0=Sun ... 6=Sat
  rate_limit_per_hour: 60, // 0 = unlimited
  webhook_url: "",
  matrix: EVENTS.reduce((acc, e) => {
    acc[e.key] = { sound: e.key !== "payout_complete", desktop: e.key === "manager_requested" || e.key === "fraud_flag", favicon: true, push: e.key === "manager_requested", webhook: false };
    return acc;
  }, {}),
};

export function getNotifPrefs() {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return { ...defaults };
    const parsed = JSON.parse(raw);
    return {
      ...defaults,
      ...parsed,
      matrix: { ...defaults.matrix, ...(parsed.matrix || {}) },
    };
  } catch { return { ...defaults }; }
}
export function setNotifPrefs(p) { localStorage.setItem(PREFS_KEY, JSON.stringify(p)); }

function isQuietNow(prefs) {
  const now = new Date();
  const wk = now.getDay();
  if ((prefs.quiet_days || []).includes(wk)) return true;
  const { quiet_start, quiet_end } = prefs;
  if (!quiet_start || !quiet_end) return false;
  const m = now.getHours() * 60 + now.getMinutes();
  const [sh, sm] = quiet_start.split(":").map(Number);
  const [eh, em] = quiet_end.split(":").map(Number);
  const s = sh * 60 + sm, e = eh * 60 + em;
  return s <= e ? (m >= s && m < e) : (m >= s || m < e);
}

function withinRateLimit(prefs) {
  const limit = prefs.rate_limit_per_hour || 0;
  if (!limit) return true;
  const log = getNotifLog();
  const hourAgo = Date.now() - 3600000;
  const count = log.filter((l) => new Date(l.at).getTime() >= hourAgo).length;
  return count < limit;
}

export function getNotifLog() {
  try { return JSON.parse(localStorage.getItem(LOG_KEY) || "[]"); } catch { return []; }
}
function appendLog(entry) {
  const log = [entry, ...getNotifLog()].slice(0, 40);
  localStorage.setItem(LOG_KEY, JSON.stringify(log));
}
export function clearLog() { localStorage.removeItem(LOG_KEY); }

export function fireAlert(event, title, body, onClick) {
  const p = getNotifPrefs();
  if (!p.enabled) return { fired: false, reason: "disabled" };
  const routing = p.matrix?.[event];
  if (!routing) return { fired: false, reason: "unknown_event" };
  if (isQuietNow(p)) return { fired: false, reason: "quiet_hours" };
  if (!withinRateLimit(p)) return { fired: false, reason: "rate_limited" };

  const fired = [];
  if (routing.sound) { playSound(p.sound_style, p.volume); fired.push("sound"); }
  if (routing.favicon) { flashFavicon(); fired.push("favicon"); }
  if (routing.desktop) { browserNotify(title, body, onClick); fired.push("desktop"); }
  if (routing.webhook && p.webhook_url) {
    fetch(p.webhook_url, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ text: `*${title}*\n${body}` }) }).catch(() => {});
    fired.push("webhook");
  }
  appendLog({ at: new Date().toISOString(), event, title, body, channels: fired });
  return { fired: true, channels: fired };
}
